import "./ProductHome.scss";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getCurrentUser } from 'aws-amplify/auth';
import EditProductModal from "./EditProductModal";

import {
  faGauge,
  faDesktop,
  faSearch,
  faPlus,
  faMagnifyingGlassChart,
  faMagnifyingGlass,
  faPenToSquare,
  faTrash,
  faEllipsisVertical,
} from "@fortawesome/free-solid-svg-icons";

const DEVICE_ID_REGEX = /^([0-9A-Fa-f]{2})([:.])([0-9A-Fa-f]{2})(?:\2[0-9A-Fa-f]{2}){4}$/;

const canonicalizeDeviceId = (value = "") => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return "";
  }

  if (DEVICE_ID_REGEX.test(trimmed)) {
    return trimmed.replace(/[:.]/g, ".").toUpperCase();
  }

  const hexOnly = trimmed.replace(/[^0-9A-Fa-f]/g, "").toUpperCase();
  if (hexOnly.length !== 12) {
    return "";
  }

  const pairs = hexOnly.match(/.{2}/g);
  return pairs.join(".");
};

const formatDeviceIdForDisplay = (value = "") => {
  const canonical = canonicalizeDeviceId(value);
  return canonical || (value ? value.toString().toUpperCase() : "");
};

const Home = () => {
  const [userId, setUserId] = useState(null);
  const [data, setData] = useState([]);
  const [logCounts, setLogCounts] = useState({});
  const [eventCounts, setEventCounts] = useState({});
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [initialLoad, setInitialLoad] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalSubMessage, setModalSubMessage] = useState("");
  const [productIdToDelete, setProductIdToDelete] = useState(null);
  const [searchText, setSearchText] = useState(""); // For search/filtering
  const [showAddEvoNetLinkModal, setShowAddEvoNetLinkModal] = useState(false);
  const [deviceId, setDeviceId] = useState("");
  const [isCheckingDeviceId, setIsCheckingDeviceId] = useState(false);
  const [deviceCheckMessage, setDeviceCheckMessage] = useState("");
  const [isDeviceAssigned, setIsDeviceAssigned] = useState(false);
  const [assignedProductId, setAssignedProductId] = useState(null);
  const [currentProductId, setCurrentProductId] = useState(null); // Define current product ID
  const [showRemoveEvoNetLinkModal, setShowRemoveEvoNetLinkModal] = useState(false);
  const [currentDeviceId, setCurrentDeviceId] = useState("");
  const [newSerialNumber, setNewSerialNumber] = useState(""); // For new serial number input
  const [newDescription, setNewDescription] = useState("");   // For new description input
  const [addEvoError, setAddEvoError] = useState("");         // For error messages in Add EVO modal
  const [showAddEvoModal, setShowAddEvoModal] = useState(false); // To show/hide the Add EVO modal
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showMobileActions, setShowMobileActions] = useState(false);
  const [mobileActionProduct, setMobileActionProduct] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1920
  );
  const [containerHeight, setContainerHeight] = useState(null);
  const homeContainerRef = useRef(null);

  const navigate = useNavigate();

  const validateSerialNumber = (serial) => {
    const regex = /^(\d{5})-(\d{2})(\d{2})-(\d{4})$/;
    const modelMap = {
      "07870": "EVO-1212F",
      "07871": "EVO-1212F-HW",
      "07872": "EVO-1224F",
      "07873": "EVO-1224F-HW",
      "07942": "EVO-2212",
      "07936": "EVO-2212E",
      "07944": "EVO-2224",
      "07938": "EVO-2224E",
      "07943": "EVO-3012",
      "07937": "EVO-3012E",
      "07945": "EVO-4024",
      "07926": "EVO-4024E",
      "07875": "EVO-4248SP",
      "08803": "EVO-30AB",
    };

    const match = serial.match(regex);
    if (!match) return { valid: false, message: "The format for serial numbers is xxxxx-yyyy-zzzz." };

    const itemNumber = match[1];
    if (!modelMap[itemNumber]) {
      return { valid: false, message: "Unknown item number in serial number." };
    }

    return { valid: true, productType: modelMap[itemNumber] };
  };

  useEffect(() => {
    const fetchUserSession = async () => {
      try {
        const { userId } = await getCurrentUser();
        setUserId(userId);

        // Start the page fetch after user and session are verified
        fetchData(userId);
      }
      catch {
        console.error("User is not logged in");
        navigate("/login");
      }
    };
    fetchUserSession();
  }, []); // Empty dependency array to run once when the component mounts

  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const pageContainer = homeContainerRef.current?.closest(".pageContainer");
    if (!pageContainer) {
      return;
    }

    pageContainer.classList.add("pageContainer--product-single-scroll");
    document.body.classList.add("product-page-scroll-lock");
    document.documentElement.classList.add("product-page-scroll-lock");

    return () => {
      pageContainer.classList.remove("pageContainer--product-single-scroll");
      document.body.classList.remove("product-page-scroll-lock");
      document.documentElement.classList.remove("product-page-scroll-lock");
    };
  }, []);

  useEffect(() => {
    const updateContainerHeight = () => {
      if (!homeContainerRef.current) {
        return;
      }

      const rect = homeContainerRef.current.getBoundingClientRect();
      const bottomGap = 12;
      const availableHeight = window.innerHeight - rect.top - bottomGap;
      setContainerHeight(Math.max(320, availableHeight));
    };

    updateContainerHeight();
    window.addEventListener("resize", updateContainerHeight);

    return () => {
      window.removeEventListener("resize", updateContainerHeight);
    };
  }, []);


  // Group the data by product number and serial number
  const groupedData = data.reduce((result, item) => {
    const key = `${item.product_type}-${item.product_serial_number}`;
    if (!result[key]) {
      result[key] = [];
    }
    result[key].push(item);
    return result;
  }, {});

  // For search/filtering
  const updateSearchText = (e) => {
    setSearchText(e.target.value); // Step 4: Update search text
  };

  const filterData = () => {
    const lowercasedFilter = searchText.toLowerCase();

    const normalizeForSearch = (value) =>
      (value === null || value === undefined ? "" : String(value)).toLowerCase();

    return Object.entries(groupedData)
      .filter(([key, value]) =>
        value.some(
          (item) =>
            normalizeForSearch(item.product_type).includes(lowercasedFilter) ||
            normalizeForSearch(item.product_serial_number).includes(lowercasedFilter) ||
            normalizeForSearch(item.product_group).includes(lowercasedFilter) ||
            normalizeForSearch(item.product_description).includes(lowercasedFilter)
        )
      )
      .reduce((obj, [key, value]) => {
        obj[key] = value;
        return obj;
      }, {});
  };

  // Apply filter before rendering
  const filteredData = filterData();

  const fetchData = async (userId) => {
    try {
      setLoadingProducts(true);

      const response = await fetch("https://sucmhxbjlk.execute-api.us-east-1.amazonaws.com/prod/product/get",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": userId,
          },
        }
      );

      if (response.ok) {
        const jsonBody = await response.json();
        const jsonData = jsonBody.data;
        console.log("fetched data:" + JSON.stringify(jsonData));

        // Ensure jsonData is an array before setting it
        if (Array.isArray(jsonData)) {
          setData(jsonData);
        } else {
          console.error("Unexpected API response:", jsonData);
          setData([]);
        }
        setLoadingProducts(false);

        // Fetch log counts for each product
        const logCounts = await fetchLogCounts(jsonData);
        //await fetchEventCounts(jsonData);

      } else {
        console.error("Failed to fetch data");
        setData([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchLogCounts = async () => {
    try {
      const response = await fetch(
        `https://sucmhxbjlk.execute-api.us-east-1.amazonaws.com/prod/product/logcount`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": userId,
          },
        }
      );
      if (response.ok) {
        const jsonBody = await response.json();
        const jsonData = jsonBody.data;

        const countLogs = jsonData.reduce((counts, event) => {
          const productId = event.product_id;
          counts[productId] = event.log_count;
          return counts;
        }, {});

        setLogCounts(countLogs);
      } else {
        console.error(`Failed to fetch log file counts`);
      }
    } catch (error) {
      console.error(
        `Error fetching log file counts`,
        error
      );
    }
  };

  const fetchEventCounts = async () => {
    try {
      const response = await fetch(
        `https://sucmhxbjlk.execute-api.us-east-1.amazonaws.com/prod/product/events`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": userId,
          },
        }
      );
      if (response.ok) {
        const jsonBody = await response.json();
        const jsonData = jsonBody.data;

        const countEvents = jsonData.reduce((counts, event) => {
          const productId = event.product_id;
          if (counts[productId]) {
            counts[productId]++;
          } else {
            counts[productId] = 1;
          }
          return counts;
        }, {});

        setEventCounts(countEvents);
      } else {
        console.error(`Failed to fetch events`);
      }
    } catch (error) {
      console.error(`Error fetching events`, error);
    }
  };

  const handleDeleteClick = (id, serial_number) => {
    setModalMessage(
      `Are you sure you want to remove the serial number "${serial_number}" from the My Products list?`
    );
    setModalSubMessage("This action cannot be undone.");
    setProductIdToDelete(id);
    setShowModal(true);
  };

  const handleModalCancel = () => {
    setShowModal(false);
    setProductIdToDelete(null);
  };

  const handleModalDelete = () => {
    if (productIdToDelete !== null) {
      deleteProductFunction(productIdToDelete);
    }
    setShowModal(false);
  };

  const handleEscKey = (event) => {
    if (event.key === "Escape") {
      handleModalCancel();
    }
  };

  useEffect(() => {
    if (showModal) {
      window.addEventListener("keydown", handleEscKey);
    } else {
      window.removeEventListener("keydown", handleEscKey);
    }

    return () => {
      window.removeEventListener("keydown", handleEscKey);
    };
  }, [showModal]);

  const deleteProductFunction = async (id) => {
    try {
      const response = await fetch(
        `https://sucmhxbjlk.execute-api.us-east-1.amazonaws.com/prod/product/delete/${id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": userId,
          }
        }
      );
      if (response.ok) {
        const jsonBody = await response.json();
        const jsonData = jsonBody.data;
        console.log("deleted data:" + JSON.stringify(jsonData));

        // Validate that jsonData is an array
        if (Array.isArray(jsonData)) {
          setData(jsonData);
        } else {
          console.error("Unexpected API response for delete:", jsonData);
          setData([]); // Fallback to an empty array
        }
      } else {
        console.error("Failed to delete product");
      }
    } catch (error) {
      console.error("Error deleting product:", error);
    }
  };

  useEffect(() => {
    if (
      initialLoad &&
      Object.keys(expandedSections).length === 0 &&
      Object.keys(groupedData).length > 0
    ) {
      const firstKey = Object.keys(groupedData)[0];
      setExpandedSections({ [firstKey]: true });
      setInitialLoad(false);
    }
  }, [expandedSections, initialLoad, groupedData]);

  const getProductImage = (productName) => {
    try {
      const EvoModels = [
        "EVO-2212",
        "EVO-2224",
        "EVO-3012",
        "EVO-4024",
        "EVO-2212E",
        "EVO-2224E",
        "EVO-3012E",
        "EVO-4024E",
      ];
      const EvoSPModels = ["EVO-4248", "EVO-4248SP"];

      let productImage = "EVO.png";
      if (EvoSPModels.includes(productName)) productImage = "EVO-SP.png";

      const imagePath = `/products/${productImage}`;
      return imagePath;
    } catch (e) {
      return `/products/default.png`;
    }
  };

  for (const key in groupedData) {
    groupedData[key].sort(
      (a, b) => new Date(b.datetime_file) - new Date(a.datetime_file)
    );
  }

  const handleAddProductClick = () => {
    setShowAddEvoModal(true); // Open the Add EVO modal
  };

  const handleAddProduct = async () => {
    const { valid, message, productType } = validateSerialNumber(newSerialNumber);
    if (!valid) {
      setAddEvoError(message); // Show validation error
      return;
    }

    try {
      const response = await fetch(
        "https://sucmhxbjlk.execute-api.us-east-1.amazonaws.com/prod/product/associate-or-add",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": userId,
          },
          body: JSON.stringify({
            serial_number: newSerialNumber, // Use the entered serial number
            description: newDescription,   // Use the entered description
            product_type: productType,     // Determined product type
          }),
        }
      );

      const result = await response.json();

      if (response.ok) {
        if (result.alreadyAssociated) {
          setAddEvoError("This serial number is already associated with your account.");
        } else {
          setAddEvoError(""); // Clear any errors
          fetchData(userId); // Refresh the product list
          setShowAddEvoModal(false); // Close the modal
          setNewSerialNumber(""); // Clear input fields
          setNewDescription("");
        }
      } else {
        setAddEvoError(result.message || "Failed to communicate with the server.");
      }
    } catch (error) {
      setAddEvoError("An error occurred while adding/updating the product.");
    }
  };

  const handleLogsClick = (product) => {
    navigate("/logs/" + product.id, { state: { product } });
  };

  const handleDebugClick = (product) => {
    navigate("/debug/" + product.id, { state: { product } });
  };

  const handleDashboardClick = (product) => {
    navigate("/dashboard/" + product.id, { state: { product } });
  };

  const handleRCPlusClick = (product) => {
    navigate("/rcplus/" + product.id, { state: { product } });
  };

  const validateDeviceId = (candidate) => {
    const canonical = canonicalizeDeviceId(candidate);
    return canonical || null;
  };

  const handleCheckDeviceId = async () => {
    setIsCheckingDeviceId(true);
    setDeviceCheckMessage("");

    const formattedDeviceId = validateDeviceId(deviceId);
    if (!formattedDeviceId) {
      setDeviceCheckMessage("Invalid Device ID format. Expected XX.XX.XX.XX.XX.XX.");
      setIsCheckingDeviceId(false);
      return;
    }

    setDeviceId(formattedDeviceId);
    try {
      const response = await fetch(
        `https://sucmhxbjlk.execute-api.us-east-1.amazonaws.com/prod/netlink/check-mac/${formattedDeviceId}`
      );
      if (response.ok) {
        const result = await response.json();
        if (result.isAssigned) {
          setIsDeviceAssigned(true);
          setAssignedProductId(result.assignedProductId);
          setDeviceCheckMessage("Device ID is already assigned.");
        } else {
          setDeviceCheckMessage("Device ID is available.");
          setIsDeviceAssigned(false);
        }
      } else {
        setDeviceCheckMessage("Failed to check Device ID.");
      }
    } catch (error) {
      setDeviceCheckMessage("Error checking Device ID.");
    } finally {
      setIsCheckingDeviceId(false);
    }
  };


  const handleMoveDeviceId = async () => {
    setIsCheckingDeviceId(true);
    try {
      const response = await fetch(
        `https://sucmhxbjlk.execute-api.us-east-1.amazonaws.com/prod/netlink/move-mac`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": userId,
          },
          body: JSON.stringify({ macId: deviceId, newProductId: currentProductId }),
        }
      );
      if (response.ok) {
        setDeviceCheckMessage("");
        setDeviceId("");
        setIsDeviceAssigned(false);
        fetchData(userId); // Refresh data after moving
        setShowAddEvoNetLinkModal(false); // Close the modal
      } else {
        setDeviceCheckMessage("Failed to move Device ID.");
      }
    } catch (error) {
      setDeviceCheckMessage("Error moving Device ID.");
    } finally {
      setIsCheckingDeviceId(false);
    }
  };

  const handleDisassociateEvoNetLink = async () => {
    try {
      const response = await fetch(
        `https://sucmhxbjlk.execute-api.us-east-1.amazonaws.com/prod/netlink/disassociate-mac/${currentProductId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "Authorization": userId,
          }
        }
      );
      if (response.ok) {
        fetchData(userId); // Refresh the product data
        setShowRemoveEvoNetLinkModal(false); // Close the modal
      } else {
        console.error("Failed to disassociate EVO-NetLink.");
      }
    } catch (error) {
      console.error("Error disassociating EVO-NetLink:", error);
    }
  };

  const handleEvoNetLinkAdd = (product) => {
    setCurrentProductId(product.id); // Set the current product for which the IoT device is being added
    setDeviceId("");
    setDeviceCheckMessage("");
    setIsDeviceAssigned(false);
    setShowAddEvoNetLinkModal(true); // Show the Add IoT Device modal
  };

  const handleEvoNetLinkDelete = (product) => {
    setCurrentProductId(product.id); // Set the product ID for which the EVO-NetLink is being removed
    setCurrentDeviceId(formatDeviceIdForDisplay(product.iot_macid));
    setShowRemoveEvoNetLinkModal(true); // Show the Remove EVO-NetLink modal
  };

  const handleEditProductClick = (product) => {
    setSelectedProduct(product);
  };

  const handleMobileMoreClick = (product) => {
    setMobileActionProduct(product);
    setShowMobileActions(true);
  };

  const closeMobileActions = () => {
    setShowMobileActions(false);
    setMobileActionProduct(null);
  };

  const handleCloseModal = () => {
    setSelectedProduct(null);
  };

  const handleSaveProduct = (updatedProduct) => {
    setData((prevData) =>
      prevData.map((p) => (p.id === updatedProduct.id ? updatedProduct : p))
    );
  };

  const showInvestigateButton = viewportWidth >= 992;
  const showAnalyzeButton = viewportWidth >= 1200;
  const showEditButton = viewportWidth >= 1440;
  const showNetLinkButton = viewportWidth >= 1680;
  const showDeleteButton = viewportWidth >= 1840;
  const hasOverflowActions =
    !showInvestigateButton ||
    !showAnalyzeButton ||
    !showEditButton ||
    !showNetLinkButton ||
    !showDeleteButton;

  useEffect(() => {
    if (!hasOverflowActions && showMobileActions) {
      closeMobileActions();
    }
  }, [hasOverflowActions, showMobileActions]);

  return (
    <div
      className="homeContainer"
      ref={homeContainerRef}
      style={containerHeight ? { height: `${containerHeight}px` } : undefined}
    >
      <div className="homeHeader">
        <div className="homeHeaderSearch">
          <div className="homeHeaderSearchIcon">
            <FontAwesomeIcon icon={faSearch} className="searchIcon" />
          </div>
          <div className="homeHeaderSearchInput">
            <input
              type="text"
              placeholder="Search/Filter..."
              onChange={updateSearchText}
            />
          </div>
        </div>
        <div className="homeHeaderAdd">
          <button onClick={handleAddProductClick}>
            <FontAwesomeIcon icon={faPlus} />
            Add EVO
          </button>
        </div>
      </div>
      <div className="homeTableContainer">
        {loadingProducts ? (
          <p>Loading...</p>
        ) : (
          <>
            {showModal && (
              <div className="modalContainer">
                <div className="modalBackdrop">
                  <div className="modalPopup">
                    <p>{modalMessage}</p>
                    <p>{modalSubMessage}</p>
                    <div className="buttonContainer">
                      <button
                        className="modalButton"
                        onClick={handleModalCancel}
                      >
                        Cancel
                      </button>
                      <button
                        className="modalButton"
                        onClick={handleModalDelete}
                      >
                        Confirm
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {showAddEvoModal && (
              <div className="modalContainer">
                <div className="modalBackdrop">
                  <div className="modalPopup">
                    <h2>Add EVO</h2>
                    <input
                      type="text"
                      placeholder="Enter Serial Number"
                      value={newSerialNumber}
                      onChange={(e) => setNewSerialNumber(e.target.value.trim())}
                    />
                    <input
                      type="text"
                      placeholder="Enter Description"
                      value={newDescription}
                      onChange={(e) => setNewDescription(e.target.value)}
                    />
                    {addEvoError && <p className="error">{addEvoError}</p>}
                    <div className="buttonContainer">
                      <button
                        onClick={() => {
                          setShowAddEvoModal(false);
                          setNewSerialNumber("");
                          setNewDescription("");
                          setAddEvoError("");
                        }}
                      >
                        Cancel
                      </button>
                      <button onClick={handleAddProduct}>Submit</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {showAddEvoNetLinkModal && (
              <div className="modalContainer">
                <div className="modalBackdrop">
                  <div className="modalPopup">
                    <h2>Add EVO-NetLink</h2>
                    <input
                      type="text"
                      placeholder="Enter Device ID (xx.xx.xx.xx.xx.xx)"
                      value={deviceId}
                      onChange={(e) => setDeviceId(e.target.value)}
                    />
                    {isCheckingDeviceId && <p>Checking Device ID...</p>}
                    {!isCheckingDeviceId && deviceCheckMessage && <p>{deviceCheckMessage}</p>}
                    <div className="buttonContainer">
                      <button
                        onClick={() => {
                          setShowAddEvoNetLinkModal(false);
                          setDeviceId("");
                          setDeviceCheckMessage("");
                          setIsDeviceAssigned(false);
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCheckDeviceId}
                        disabled={isCheckingDeviceId || !deviceId.trim()}
                      >
                        Check Device ID
                      </button>
                    </div>
                    {isDeviceAssigned && (
                      <div className="confirmationContainer">
                        <p>This Device ID is already assigned to another product. Do you want to move it?</p>
                        <div className="confirmationButtons">
                          <button className="secondaryButton" onClick={() => setIsDeviceAssigned(false)}>No</button>
                          <button className="primaryButton" onClick={handleMoveDeviceId}>Yes, Move</button>
                        </div>
                      </div>
                    )}
                    {!isDeviceAssigned && deviceCheckMessage === "Device ID is available." && (
                      <div className="confirmationContainer">
                        <p>The Device ID is available. Would you like to associate it with this product?</p>
                        <div className="confirmationButtons">
                          <button className="primaryButton" onClick={handleMoveDeviceId}>Yes, Associate</button>
                          <button
                            className="secondaryButton"
                            onClick={() => {
                              setShowAddEvoNetLinkModal(false);
                              setDeviceId("");
                              setDeviceCheckMessage("");
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {showRemoveEvoNetLinkModal && (
              <div className="modalContainer">
                <div className="modalBackdrop">
                  <div className="modalPopup">
                    <h2>Remove EVO-NetLink</h2>
                    <p>Device ID: <strong>{currentDeviceId || "Unknown"}</strong></p>
                    <p>Are you sure you want to remove this EVO-NetLink from the product?</p>
                    <p>This action cannot be undone.</p>
                    <div className="buttonContainer">
                      <button onClick={() => setShowRemoveEvoNetLinkModal(false)}>
                        Cancel
                      </button>
                      <button onClick={handleDisassociateEvoNetLink}>
                        Confirm
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="productCardList">
              {Object.keys(filteredData).length > 0 ? (
                Object.keys(filteredData).map((key, index) => {
                  const product = groupedData[key][0];
                  const hasNetLink = !!product.iot_macid;
                  const hasLogCount = logCounts[product.id] !== undefined && logCounts[product.id] !== null;

                  return (
                    <div className="mobileProductCard" key={`mobile-${index}`}>
                      <div className="mobileCardHeader">
                        <div className="mobileCardProduct">
                          <img
                            src={getProductImage(product.product_type)}
                            alt={product.product_type}
                            width="56"
                            height="56"
                          />
                          <div className="mobileCardTitleBlock">
                            <h3>{product.product_type}</h3>
                            <span className="mobileCardSerial">{product.product_serial_number}</span>
                          </div>
                        </div>
                        {hasLogCount && (
                          <div className="mobileCardLogCountTop">
                            <span className="label">Log Count:</span>
                            <span>{logCounts[product.id]}</span>
                          </div>
                        )}
                      </div>

                      {hasNetLink && (
                        <div className="mobileCardMeta">
                          <div>
                            <span className="label">NetLink ID:</span>
                            <span>{formatDeviceIdForDisplay(product.iot_macid)}</span>
                          </div>
                        </div>
                      )}

                      <p className="mobileCardDescription">{product.product_description}</p>

                      {hasLogCount && (
                        <div className="desktopCardLogCount">
                          <span className="label">Log Count:</span>
                          <span>{logCounts[product.id]}</span>
                        </div>
                      )}

                      <div className="mobileCardActions">
                        <button
                          className={`button ${!hasNetLink ? "button-disabled" : ""}`}
                          onClick={() => handleDashboardClick(product)}
                          disabled={!hasNetLink}
                        >
                          <FontAwesomeIcon icon={faGauge} />
                          Dashboard
                        </button>
                        <button
                          className={`button ${!hasNetLink ? "button-disabled" : ""}`}
                          onClick={() => handleRCPlusClick(product)}
                          disabled={!hasNetLink}
                        >
                          <FontAwesomeIcon icon={faDesktop} />
                          RC+
                        </button>
                        {showInvestigateButton && (
                          <button
                            className="button"
                            onClick={() => handleDebugClick(product)}
                          >
                            <FontAwesomeIcon icon={faMagnifyingGlass} />
                            Investigate
                          </button>
                        )}
                        {showAnalyzeButton && (
                          <button
                            className="button"
                            onClick={() => handleLogsClick(product)}
                          >
                            <FontAwesomeIcon icon={faMagnifyingGlassChart} />
                            Analyze
                          </button>
                        )}
                        {showEditButton && (
                          <button
                            className="button"
                            onClick={() => handleEditProductClick(product)}
                          >
                            <FontAwesomeIcon icon={faPenToSquare} />
                            Edit
                          </button>
                        )}
                        {showNetLinkButton && (hasNetLink ? (
                          <button
                            className="button"
                            onClick={() => handleEvoNetLinkDelete(product)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                            Remove NetLink
                          </button>
                        ) : (
                          <button
                            className="button"
                            onClick={() => handleEvoNetLinkAdd(product)}
                          >
                            <FontAwesomeIcon icon={faPlus} />
                            Add NetLink
                          </button>
                        ))}
                        {showDeleteButton && (
                          <button
                            className="button"
                            onClick={() =>
                              handleDeleteClick(
                                product.id,
                                product.product_serial_number
                              )
                            }
                          >
                            <FontAwesomeIcon icon={faTrash} />
                            Delete EVO
                          </button>
                        )}
                        {hasOverflowActions && (
                          <button className="button moreButton" onClick={() => handleMobileMoreClick(product)}>
                            <FontAwesomeIcon icon={faEllipsisVertical} />
                            More
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="mobileNoData">No data available</div>
              )}
            </div>

            {showMobileActions && mobileActionProduct && hasOverflowActions && (
              <div className="modalContainer">
                <div className="modalBackdrop" onClick={closeMobileActions}>
                  <div className="modalPopup mobileActionSheet" onClick={(e) => e.stopPropagation()}>
                    <h2>More Actions</h2>
                    <div className="mobileActionList">
                      {!showInvestigateButton && (
                        <button
                          className="button"
                          onClick={() => {
                            handleDebugClick(mobileActionProduct);
                            closeMobileActions();
                          }}
                        >
                          <FontAwesomeIcon icon={faMagnifyingGlass} />
                          Investigate
                        </button>
                      )}
                      {!showAnalyzeButton && (
                        <button
                          className="button"
                          onClick={() => {
                            handleLogsClick(mobileActionProduct);
                            closeMobileActions();
                          }}
                        >
                          <FontAwesomeIcon icon={faMagnifyingGlassChart} />
                          Analyze
                        </button>
                      )}
                      {!showEditButton && (
                        <button
                          className="button"
                          onClick={() => {
                            handleEditProductClick(mobileActionProduct);
                            closeMobileActions();
                          }}
                        >
                          <FontAwesomeIcon icon={faPenToSquare} />
                          Edit
                        </button>
                      )}
                      {!showNetLinkButton && (mobileActionProduct.iot_macid ? (
                        <button
                          className="button"
                          onClick={() => {
                            handleEvoNetLinkDelete(mobileActionProduct);
                            closeMobileActions();
                          }}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                          Remove NetLink
                        </button>
                      ) : (
                        <button
                          className="button"
                          onClick={() => {
                            handleEvoNetLinkAdd(mobileActionProduct);
                            closeMobileActions();
                          }}
                        >
                          <FontAwesomeIcon icon={faPlus} />
                          Add NetLink
                        </button>
                      ))}
                      {!showDeleteButton && (
                        <button
                          className="button"
                          onClick={() => {
                            handleDeleteClick(
                              mobileActionProduct.id,
                              mobileActionProduct.product_serial_number
                            );
                            closeMobileActions();
                          }}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                          Delete EVO
                        </button>
                      )}
                      <button className="button" onClick={closeMobileActions}>
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {selectedProduct && (
              <EditProductModal
                product={selectedProduct}
                onClose={handleCloseModal}
                onSave={handleSaveProduct}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Home;
